import { PipelineKlassApi } from '@app/api';
import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import InlineEdit from '@app/components/InLineEdit';
import { useInstructorOptions } from '@app/components/InstructorName';
import { ClassModalityLabel, ClassTypeLabel, InstructorNames, RequestStatusLabel, formatStatusText } from '@app/components/KlassLabels';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { KLASS_MODALITY, KLASS_TYPE, REQUEST_STATUS } from '@app/model/Enums';
import { Instructor } from '@app/model/Instructor';
import { PipelineKlass } from '@app/model/PipelineKlass';
import {
  useClientExecutiveStore, useCountryStore, useCourseStore, useInstructorStore, useLanguageStore,
  usePipelineKlassStore, useTrainingSpecialistStrategicStore
} from '@app/store/entityStore';
import { NotFoundState } from '@app/components/NotFoundState';
import { isNotFound } from '@app/utils/ApiError';
import { ddMMyyyyFormat, ddMMyyyyHHmmFormat, ddMMyyyyParse, toIsoDate } from '@app/utils/functions';
import {
  ActionList, ActionListItem, Alert, AlertVariant, Button, DescriptionList,
  DescriptionListDescription, DescriptionListGroup, DescriptionListTerm, FlexItem, Icon, Modal,
  ModalBody, ModalFooter, ModalHeader, ModalVariant, PageSection, Title
} from '@patternfly/react-core';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';
import * as React from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

/**
 * One pipeline request, and the page it is worked on. Also where a request is raised by hand, at
 * `/classes/pipeline/new`.
 *
 * Addressed by the row id rather than by `requestId`, unlike a class which is reached by its
 * offering id: the request id is the business key but it is also editable here, and a page whose URL
 * changes under an edit is a page whose Back button lands on a 404.
 *
 * Everything is editable except the three the request cannot own: `id`; `creationDate`, which is
 * when the request was raised; and `requestId`, the key the import upserts on and the hinge to the
 * class the request becomes, which is therefore the raising system's to assign and nobody's here to
 * change.
 *
 * The create form differs from the stored request in shape as well as in what it writes: it opens
 * every field at once rather than one pencil at a time, since a request that does not exist yet is
 * something to fill in, not something to amend.
 */

/** A LocalDate field, held on the wire as "yyyy-MM-dd". */
type IsoDate = PipelineKlass['deliveryDate'];

/**
 * What InlineEdit's date picker hands back - "dd/MM/yyyy" - as the ISO date the service takes.
 * Null for a cleared field: the service will not deserialise an empty string into a date.
 */
const toStoredDate = (picked: unknown): IsoDate | null => {
  const text = String(picked ?? '').trim();
  if (!text) return null;
  const parsed = ddMMyyyyParse(text);
  return isNaN(parsed.getTime()) ? null : (toIsoDate(parsed) as unknown as IsoDate);
};

/**
 * Now, as the "yyyy-MM-ddTHH:mm:ss" the service's LocalDateTime takes.
 *
 * Not toISOString(), for the reason toIsoDate is not either: that converts to UTC, and the service
 * reads the wall clock off whatever it is sent - so a request raised at 15:30 in Lisbon in summer
 * would be recorded as having been raised at 14:30.
 */
const nowAsLocalDateTime = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${toIsoDate(now)}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

/**
 * What a request being raised by hand starts as: pending, and nothing else decided yet.
 *
 * Pending is where every request begins whoever raises it, so the status is shown settled rather
 * than asked for - there is no other answer to give on a request that does not exist yet.
 */
const blankRequest = (): PipelineKlass => ({ requestStatus: REQUEST_STATUS.PENDING } as PipelineKlass);

const PipelineDetail: React.FunctionComponent = () => {
  type Action = { type: 'load' | 'update', payload: PipelineKlass };

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  /** The create form rather than a stored request: no record to load, and nothing to refresh. */
  const isNew = id === 'new';

  const loadRequest = usePipelineKlassStore(state => state.loadById);
  const saveRequest = usePipelineKlassStore(state => state.save);

  const { items: instructors } = useEntity(useInstructorStore);
  const { items: courses } = useEntity(useCourseStore);
  const { items: languages } = useEntity(useLanguageStore);
  const { items: countries } = useEntity(useCountryStore);
  const { items: clientExecutives } = useEntity(useClientExecutiveStore);
  const { items: tsses } = useEntity(useTrainingSpecialistStrategicStore);

  const [isDirty, setIsDirty] = React.useState(false);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);

  // Loading a record replaces the form wholesale and so ends whatever was unsaved in it, whether it
  // came from the service, from Cancel, or from the create below - which is what lets the create
  // navigate to the new request without the blocker treating it as an abandoned edit.
  const requestUpdater = (state: PipelineKlass, action: Action) => {
    switch (action.type) {
      case 'load':
        setIsDirty(false);
        return action.payload;
      case 'update':
        setIsDirty(true);
        return action.payload;
      default: return state;
    }
  };

  const [request, setRequest] = React.useReducer(requestUpdater, isNew ? blankRequest() : {} as PipelineKlass);

  const updateRequest = React.useCallback((updated: PipelineKlass) => {
    setRequest({ type: 'update', payload: updated });
  }, []);

  useBreadcrumbLabel(isNew ? 'New' : request.requestId);
  // The overview is unmounted, so this page has to name the crumb above it as well
  useBreadcrumbLabel('Pipeline', '/classes/pipeline');

  /** No request answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState(false);

  const fetchRequest = React.useCallback(async (bypassCache = false) => {
    if (!id || id === 'new') return;
    setNotFound(false);
    try {
      // Cloned so editing the form does not mutate the copy the store is holding
      const record = await loadRequest(id, bypassCache);
      setRequest({ type: 'load', payload: structuredClone(record) });
    } catch (err: any) {
      // A mistyped or stale id is the answer rather than a failure: the page renders it.
      if (isNotFound(err)) {
        setNotFound(true);
        return;
      }
      addNotification('Error loading pipeline request', AlertVariant.danger, err.message);
    }
    // addNotification is stable for the life of the provider; loadRequest is a store selector
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadRequest]);

  React.useEffect(() => {
    void fetchRequest();
  }, [fetchRequest]);

  // Warns on a full reload, which the router's blocker below cannot see
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  // --- Option lists ------------------------------------------------------------------
  // Built once per dictionary rather than per field. The selects hand back ids, so each one maps
  // back to the store's own copy on the way in, keeping the request pointing at shared entities.
  // Per course, with each instructor's certification for it - see useInstructorOptions. A
  // request whose SKU matched no course has no course to judge against, and the options are
  // then plain names.
  const instructorOptionsFor = useInstructorOptions(instructors);

  const courseOptions = React.useMemo(
    () => courses.map(c => ({ value: c.id, content: `${c.sku} - ${c.name}` })),
    [courses]
  );

  const languageOptions = React.useMemo(
    () => languages.map(l => ({ value: l.id, content: l.englishName })),
    [languages]
  );

  const countryOptions = React.useMemo(
    () => countries.map(c => ({ value: c.id, content: `${c.flag} ${c.name}` })),
    [countries]
  );

  const clientExecutiveOptions = React.useMemo(
    () => clientExecutives.map(ce => ({ value: ce.id, content: ce.name })),
    [clientExecutives]
  );

  const tssOptions = React.useMemo(
    () => tsses.map(t => ({ value: t.id, content: t.name })),
    [tsses]
  );

  const selectedInstructorIds = React.useMemo(
    () => (request.instructors ?? []).map(i => i.id),
    [request.instructors]
  );

  const toInstructors = React.useCallback(
    (ids: any): Instructor[] =>
      ((ids ?? []) as (string | number)[])
        .map(instructorId => instructors.find(i => i.id === instructorId))
        .filter(Boolean) as Instructor[],
    [instructors]
  );

  /** Enum options, labelled the way the rest of the app labels statuses. */
  const enumOptions = (values: string[]) => values.map(v => ({ value: v, content: formatStatusText(v) }));

  /**
   * While creating, the page is a form: every field open at once, filled in top to bottom and
   * committed in one go. A stored request is amended a field at a time instead, each behind its own
   * pencil, so that nothing is changed by simply passing through it.
   */
  const formMode = { isGlobalEdition: isNew };

  // --- Actions -----------------------------------------------------------------------

  /**
   * Writes the request, then reads it back.
   *
   * The PUT is called directly rather than through the store's `save`, because
   * `PUT /pipelineclass/{id}` answers 204 with no body: there is no saved record in the response to
   * ingest, and handing the store an empty object would put an id-less row in the list. So the write
   * and the refresh are two calls, and the refresh is what updates the cache - with the request as
   * the service actually stored it, instructors reconciled and version bumped, rather than with the
   * copy this page sent.
   */
  const onSave = async () => {
    beginLoading();
    try {
      await PipelineKlassApi.update(request.id, request);
      await fetchRequest(true);
      addNotification('Pipeline request saved with success!', AlertVariant.success);
    } catch (err: any) {
      addNotification('Error saving pipeline request', AlertVariant.danger, err.message);
    } finally {
      endLoading();
    }
  };

  /**
   * Raises the request, then hands the page over to the record the service stored.
   *
   * Written through the store rather than the API, unlike the update above: POST answers 201 with
   * the stored record, so there is something to ingest, and ingesting it is what puts the new row in
   * the overview's list without a round trip.
   *
   * `creationDate` is stamped here because the service does not: `POST /pipelineclass` takes an
   * empty body and fills nothing in. For a request raised by hand, created is now.
   */
  const onCreate = async () => {
    beginLoading();
    try {
      const created = await saveRequest(
        { ...request, creationDate: nowAsLocalDateTime() as unknown as PipelineKlass['creationDate'] },
        true
      );
      setRequest({ type: 'load', payload: structuredClone(created) });
      addNotification('Pipeline request created with success!', AlertVariant.success);
      setCreatedId(created.id);
    } catch (err: any) {
      addNotification('Error creating pipeline request', AlertVariant.danger, err.message);
    } finally {
      endLoading();
    }
  };

  /**
   * Off /new and onto the request's own URL, once the render that cleared `isDirty` has committed -
   * from an effect for exactly that reason, since navigating inside the save would be judged by the
   * blocker against the state as it was before it.
   */
  const [createdId, setCreatedId] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (createdId !== null) navigate(`/classes/pipeline/${createdId}`, { replace: true });
  }, [createdId, navigate]);

  // Back to the state the page opened in: an empty form while creating, the stored request otherwise
  const onCancelEdit = () => {
    if (isNew) {
      setRequest({ type: 'load', payload: blankRequest() });
      return;
    }
    if (isDirty) void fetchRequest();
  };

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="pipeline request"
        identifier={id}
        backTo={{ path: '/classes/pipeline', label: 'Back to pipeline' }}
      />
    );
  }

  if (!isNew && !request.id) return null;

  const changeLogTitle = request.course?.sku || request.requestId;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = request.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="pipelineclass"
      id={request.id}
      entityLabel="this request"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        // The course is what the request is for, so its SKU names the page - falling back to the
        // request id for the one request that has no course, the SKU that matched nothing.
        title={isNew ? 'New Request' : (request.course?.sku || request.requestId || `Request ${request.id}`)}
        subtitle={request.course?.name}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" id="save-button" isDisabled={!isDirty}
                onClick={isNew ? onCreate : onSave}>
                {isNew ? 'Create' : 'Save'}
              </Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" id="cancel-button" isDisabled={!isDirty} onClick={onCancelEdit}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              {/* No Remove: a request raised by the system that owns it would simply be re-imported
                  on the next message carrying the same request id. Refresh does nothing while
                  creating - fetchRequest has no stored record to re-read. */}
              <PageActionsMenu onRefresh={() => fetchRequest(true)} items={changeLogItems} />
            </ActionListItem>
          </ActionList>
        }
      >
        {/* An unset field reads "N/A" beside the title, which says something about a stored request
            but nothing at all about a form that has only just been opened - so while creating, each
            label waits until it has a value to show. */}
        {(!isNew || request.requestStatus) && <FlexItem><RequestStatusLabel status={request.requestStatus} /></FlexItem>}
        {(!isNew || request.type) && <FlexItem><ClassTypeLabel type={request.type} /></FlexItem>}
        {(!isNew || request.modality) && <FlexItem><ClassModalityLabel modality={request.modality} /></FlexItem>}
      </DetailPageHeader>

      {/* The one incompleteness the import allows through on purpose: a SKU that matched no course
          leaves the request saved and importable, and needs the course picking by hand. Not shown
          while creating, where every field starts unset and none of them is news. */}
      {!isNew && !request.course && (
        <PageSection hasBodyWrapper isWidthLimited>
          <Alert variant="warning" isInline title="This request has no course">
            The SKU it arrived with did not match any course, so it was imported without one. Set the
            course below, then Save.
          </Alert>
        </PageSection>
      )}

      <PageSection hasBodyWrapper isWidthLimited>
        <DescriptionList columnModifier={{ default: '3Col' }}>
          <DescriptionListGroup>
            {/* Settable while the request is being raised and fixed from then on: it is what the
                import upserts on, so changing it later severs the request from its own system. */}
            {/* Fixed on both sides of the create: it is the key the import upserts on and the hinge
                to the class the request becomes, so it belongs to the system that raises one. Shown
                while creating, disabled, rather than hidden - the field is part of the record even
                when it is the raising system that fills it in. */}
            {isNew ? (
              <InlineEdit label='Request ID' type='TextInput' {...formMode} isDisabled
                value={request.requestId || ''}
                decorator={() => request.requestId || 'Not set'}
                onSave={() => { /* disabled: there is nothing to write back */ }}
                id="requestId" />
            ) : (
              <React.Fragment>
                <DescriptionListTerm>Request ID</DescriptionListTerm>
                <DescriptionListDescription>{request.requestId || 'N/A'}</DescriptionListDescription>
              </React.Fragment>
            )}
          </DescriptionListGroup>
          <DescriptionListGroup>
            {/* Settled at Pending while creating - see blankRequest - and freely changed from then on */}
            <InlineEdit label='Request Status' type='FilterableSelect' {...formMode} isDisabled={isNew}
              value={request.requestStatus ?? ''}
              decorator={() => <RequestStatusLabel status={request.requestStatus} />}
              // Confirming with no option picked clears the field. Send null rather than '' —
              // the backend enum will not deserialize an empty string.
              onSave={(status) => updateRequest({ ...request, requestStatus: (status || null) as unknown as REQUEST_STATUS })}
              id="requestStatus"
              options={enumOptions(Object.values(REQUEST_STATUS))} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Created</DescriptionListTerm>
            <DescriptionListDescription>
              {/* Stamped by the create, so it reads "Not set" only for as long as the form is open */}
              {ddMMyyyyHHmmFormat(request.creationDate) || 'Not set'}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Class Type Requested' type='FilterableSelect' {...formMode}
              value={request.type ?? ''}
              decorator={() => <ClassTypeLabel type={request.type} />}
              onSave={(type) => updateRequest({ ...request, type: (type || null) as unknown as KLASS_TYPE })}
              id="type"
              options={enumOptions(Object.values(KLASS_TYPE))} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Modality' type='FilterableSelect' {...formMode}
              value={request.modality ?? ''}
              decorator={() => <ClassModalityLabel modality={request.modality} />}
              onSave={(modality) => updateRequest({ ...request, modality: (modality || null) as unknown as KLASS_MODALITY })}
              id="modality"
              options={enumOptions(Object.values(KLASS_MODALITY))} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Customer Name' type='TextInput' {...formMode}
              value={request.customerName || ''}
              decorator={() => request.customerName || 'N/A'}
              onSave={(name) => updateRequest({ ...request, customerName: name as string })}
              id="customerName" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Course' type='FilterableSelect' {...formMode}
              value={request.course?.id ?? ''}
              decorator={() => request.course ? `${request.course.sku} - ${request.course.name}` : 'Not set'}
              onSave={(courseId) => updateRequest({ ...request, course: courses.find(c => c.id === courseId)! })}
              id="course"
              options={courseOptions} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Language' type='FilterableSelect' {...formMode}
              value={request.language?.id ?? ''}
              decorator={() => request.language?.englishName || 'N/A'}
              onSave={(languageId) => updateRequest({ ...request, language: languages.find(l => l.id === languageId)! })}
              id="language"
              options={languageOptions} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Instructors Requested' type='MultiFilterableSelect' {...formMode}
              placeholder="Select one or more instructors"
              value={selectedInstructorIds}
              decorator={() => <InstructorNames instructors={request.instructors} course={request.course} />}
              onSave={(ids) => updateRequest({ ...request, instructors: toInstructors(ids) })}
              id="instructors"
              options={instructorOptionsFor(request.course, request.instructors)} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Delivery Date' type='Date' {...formMode}
              value={ddMMyyyyFormat(request.deliveryDate)}
              decorator={() => ddMMyyyyFormat(request.deliveryDate) || 'Not set'}
              onSave={(picked) => updateRequest({ ...request, deliveryDate: toStoredDate(picked)! })}
              id="deliveryDate" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Alternative Date' type='Date' {...formMode}
              value={ddMMyyyyFormat(request.alternativeDate)}
              decorator={() => ddMMyyyyFormat(request.alternativeDate) || 'Not set'}
              onSave={(picked) => updateRequest({ ...request, alternativeDate: toStoredDate(picked)! })}
              id="alternativeDate" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Alternative Date 2' type='Date' {...formMode}
              value={ddMMyyyyFormat(request.alternativeDate2)}
              decorator={() => ddMMyyyyFormat(request.alternativeDate2) || 'Not set'}
              onSave={(picked) => updateRequest({ ...request, alternativeDate2: toStoredDate(picked)! })}
              id="alternativeDate2" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='City' type='TextInput' {...formMode}
              value={request.city || ''}
              decorator={() => request.city || 'N/A'}
              onSave={(city) => updateRequest({ ...request, city: city as string })}
              id="city" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Country' type='FilterableSelect' {...formMode}
              value={request.country?.id ?? ''}
              decorator={() => request.country?.name || 'N/A'}
              onSave={(countryId) => updateRequest({ ...request, country: countries.find(c => c.id === countryId)! })}
              id="country"
              options={countryOptions} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='TSS' type='FilterableSelect' {...formMode}
              value={request.tss?.id ?? ''}
              decorator={() => request.tss?.name || 'N/A'}
              onSave={(tssId) => updateRequest({ ...request, tss: tsses.find(t => t.id === tssId)! })}
              id="tss"
              options={tssOptions} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Client Executive' type='FilterableSelect' {...formMode}
              value={request.clientExecutive?.id ?? ''}
              decorator={() => request.clientExecutive?.name || 'N/A'}
              onSave={(ceId) => updateRequest({ ...request, clientExecutive: clientExecutives.find(ce => ce.id === ceId)! })}
              id="clientExecutive"
              options={clientExecutiveOptions} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Remarks' type='TextArea' {...formMode}
              value={request.remarks || ''}
              decorator={() => request.remarks || 'N/A'}
              onSave={(remarks) => updateRequest({ ...request, remarks: remarks as string })}
              id="remarks" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='TSS Comments' type='TextArea' {...formMode}
              value={request.tssComments || ''}
              decorator={() => request.tssComments || 'N/A'}
              onSave={(comments) => updateRequest({ ...request, tssComments: comments as string })}
              id="tssComments" />
          </DescriptionListGroup>
        </DescriptionList>
      </PageSection>

      <Modal
        variant={ModalVariant.small}
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.state === 'blocked' && blocker.reset()}
      >
        <ModalHeader>
          <Title headingLevel="h1" size="xl" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon status="warning" size='xl'><ExclamationTriangleIcon /></Icon>
            Unsaved Changes
          </Title>
        </ModalHeader>
        <ModalBody>
          You have unsaved changes on this request. Are you sure you want to leave this page? Your
          changes will be lost.
        </ModalBody>
        <ModalFooter>
          <Button key="confirm" variant="danger" onClick={() => blocker.state === 'blocked' && blocker.proceed()}>
            Leave without saving
          </Button>
          <Button key="cancel" variant="link" onClick={() => blocker.state === 'blocked' && blocker.reset()}>
            Stay on page
          </Button>
        </ModalFooter>
      </Modal>
    </EntityChangeLog>
  );
};

export { PipelineDetail };
